import { Box, TextField, InputAdornment, Button, Popover } from '@mui/material';
import { useState, useRef, useEffect, useCallback } from 'react';
import { type ColorResult, ChromePicker } from 'react-color';
import { useIntl } from 'react-intl';

type ColorPickerProps = {
  initialColor?: string;
  onSave?: (color: string) => void;
  showSaveButton?: boolean;
};

const useColorPicker = (initialColor: string = '#c81f5b', onSave?: (color: string) => void) => {
  const [color, setColor] = useState<string>(initialColor);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setColor(initialColor);
  }, [initialColor]);

  const handleColorChange = useCallback((newColor: ColorResult) => {
    setColor(newColor.hex);
  }, []);

  const handleTextChange = useCallback((newColor: string) => {
    setColor(newColor);
  }, []);

  const togglePicker = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const closePicker = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(color);
    }
  }, [color, onSave]);

  return {
    color,
    open,
    anchorRef,
    handleColorChange,
    handleTextChange,
    togglePicker,
    closePicker,
    handleSave,
  };
};

const ColorPreview = ({ color }: { color: string }) => (
  <Box
    sx={{
      width: 20,
      height: 20,
      bgcolor: color,
      border: '1px solid rgba(0,0,0,0.2)',
      borderRadius: '4px',
    }}
  />
);

type ColorInputProps = {
  label: string;
  color: string;
  onColorChange: (color: string) => void;
};

const ColorInput = ({ label, color, onColorChange }: ColorInputProps) => (
  <TextField
    label={label}
    value={color}
    onChange={e => onColorChange(e.target.value)}
    slotProps={{
      input: {
        sx: { height: '2.7rem' },
        startAdornment: (
          <InputAdornment position="start">
            <ColorPreview color={color} />
          </InputAdornment>
        ),
      },
    }}
  />
);

type ColorPickerPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  color: string;
  onClose: () => void;
  onColorChange: (color: ColorResult) => void;
};

const ColorPickerPopover = ({
  open,
  anchorEl,
  color,
  onClose,
  onColorChange,
}: ColorPickerPopoverProps) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  >
    <ChromePicker color={color} onChange={onColorChange} />
  </Popover>
);

export const ColorPicker = ({
  initialColor = '#c81f5b',
  onSave,
  showSaveButton = true,
}: ColorPickerProps) => {
  const intl = useIntl();
  const {
    color,
    open,
    anchorRef,
    handleColorChange,
    handleTextChange,
    togglePicker,
    closePicker,
    handleSave,
  } = useColorPicker(initialColor, onSave);

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <ColorInput
        label={intl.formatMessage({
          defaultMessage: 'Vybraná barva',
          id: 'muiPicker.selectedColor',
        })}
        color={color}
        onColorChange={handleTextChange}
      />
      <Button variant="outlined" ref={anchorRef} onClick={togglePicker}>
        {intl.formatMessage({ defaultMessage: 'Zvolit', id: 'muiPicker.choose' })}
      </Button>
      {showSaveButton && (
        <Button variant="outlined" onClick={handleSave}>
          {intl.formatMessage({ defaultMessage: 'Uložit', id: 'muiPicker.save' })}
        </Button>
      )}
      <ColorPickerPopover
        open={open}
        anchorEl={anchorRef.current}
        color={color}
        onClose={closePicker}
        onColorChange={handleColorChange}
      />
    </Box>
  );
};
